import React, { useCallback, useEffect, useMemo, useState } from "react";

import moment from "moment";

import {
  formatNumber,
  getEnvironment,
  isImpersonating,
  useFeatureFlag,
  useGetFarmReportingYear,
  useGetPracticeOrganicFertilizers,
  useGetPracticeSeedings,
  useGetPracticeTillages,
  useTranslation,
  useUserContext,
} from "utils";

import { FEATURE_FLAGS } from "consts";

import { CropsWithMissingHistoricalPracticeDataByYear, FieldListField, FieldPracticeResponse, PRACTICE_SLUG, PracticeSlug, ZERO_UUID } from "models";

import { BulkActionBar, Button, Checkbox, Flex, Icon, IconButton, MissingDataTypes, MultiCropSelectionBarMissingData, Table, Typography } from "components";

import { NoMatchesFeedback } from "pages/app/all-field-view/components/NoMatchesFeedback";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { AddEditMissingDataDrawer, MissingDataForm } from "pages/app/historical-practice-data-table-view/components/AddEditMissingDataDrawer.tsx";
import { FieldMap } from "pages/app/historical-practice-data-table-view/components/FieldMap";
import { PracticeChip } from "pages/app/historical-practice-data-table-view/components/PracticeChip";
import { PracticeColumn } from "pages/app/historical-practice-data-table-view/components/PracticeColumn";
import type { HistoricalPracticeFiltersState } from "pages/app/historical-practice-data-table-view/components/PracticeFilters";
import { PracticeTableTooltip } from "pages/app/historical-practice-data-table-view/components/PracticeTableTooltip";
import { useMissingDataTableSelection } from "pages/app/historical-practice-data-table-view/hooks/useMissingDataTableSelection";
import { getBulkPracticeMenuItems } from "pages/app/historical-practice-data-table-view/utils/BulkPracticeMenuUtils";
import { getEditableState } from "pages/app/historical-practice-data-table-view/utils/HistoricalDataUtils.ts";

type MissingDataTableProps = {
  allFieldPractices?: Record<string, FieldPracticeResponse>;
  tableBodyHeight: number;
  requiredPracticeSlugs: PracticeSlug[];
  cropsWithMissingHistoricalPracticeDataByYear: CropsWithMissingHistoricalPracticeDataByYear;
  selectedYear: number | undefined;
  fields: FieldListField[] | undefined;
  filters: HistoricalPracticeFiltersState;
  hasFiltersActive?: boolean;
  onClearFilters?: () => void;
  onDatesCollected?: (key: string, seedingStart: string, seedingEnd: string, harvestStart: string, harvestEnd: string) => void;
  skipDbMutation?: boolean;
  hidePracticeButtons?: boolean;
  hideCropSubtext?: boolean;
  clearSelectionsOnSubmit?: boolean;
};

export const MissingDataTable = ({
  tableBodyHeight,
  requiredPracticeSlugs,
  cropsWithMissingHistoricalPracticeDataByYear,
  selectedYear,
  fields,
  filters,
  hasFiltersActive,
  onClearFilters,
  onDatesCollected,
  skipDbMutation,
  hidePracticeButtons,
  hideCropSubtext,
  clearSelectionsOnSubmit,
}: MissingDataTableProps) => {
  const { t, i18n } = useTranslation(["historicalPracticeDataTableView", "practices", "allFieldView"]);
  const cropLabel = useCallback(
    (slug: string, fallback: string) => (i18n.exists(`practices:crops.${slug}`) ? t(`practices:crops.${slug}`) : fallback),
    [t, i18n]
  );
  const { user } = useUserContext();
  const {
    data: { activeFarmReportingYear, isReportingYearLocked },
  } = useGetFarmReportingYear(user?.farm.id || null);
  const { submissionStatusValue } = useSubmissionStatus();
  const isAdminOrImpersonator = getEnvironment().admin || isImpersonating();

  const [showDrawerMissingDataDrawer, setShowDrawerMissingDataDrawer] = useState<boolean>(false);
  const [drawerItems, setDrawerItems] = useState<MissingDataForm[]>([]);
  const [drawerShouldValidateOnMount, setDrawerShouldValidateOnMount] = useState<boolean>(false);
  const [addTillageEnabled, setAddTillageEnabled] = useState<boolean>(false);
  const [addSeedingEnabled, setAddSeedingEnabled] = useState<boolean>(false);
  const [addOrganicFertilizerEnabled, setAddOrganicFertilizerEnabled] = useState<boolean>(false);
  const [addGrowingPeriodEnabled, setAddGrowingPeriodEnabled] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<PracticeSlug>();

  const { isEnabled: isBulkEditingImprovementsEnabled } = useFeatureFlag(FEATURE_FLAGS.BULK_EDITING_IMPROVEMENTS);

  const { data: seedingPractices } = useGetPracticeSeedings({ enabled: false });
  const { data: tillagePractices } = useGetPracticeTillages({ enabled: false });
  const { data: organicFertilizerPractices } = useGetPracticeOrganicFertilizers({ enabled: false });

  const onAddMissingItemClicked = (missingDataForm: MissingDataForm, practiceType: PracticeSlug, shouldValidateOnMount: boolean) => {
    setDrawerItems([missingDataForm]);
    setSelectedType(practiceType);
    setDrawerShouldValidateOnMount(shouldValidateOnMount);
    setShowDrawerMissingDataDrawer(true);
  };

  const filteredEntries = useMemo(() => {
    if (!selectedYear) return [];
    const entries = cropsWithMissingHistoricalPracticeDataByYear[selectedYear] ?? [];

    const cropIdSet = new Set(filters.appliedCropIds);
    const previousCropIdSet = new Set(filters.previouslyAppliedCropIds);

    const hasPracticeFilter = filters.missingSeedings || filters.missingTillages || filters.missingOrganicFertilizers;
    const isMissingArray = <T,>(value: T[] | null | undefined) => Array.isArray(value) && value.length === 0;

    return entries.filter((entry) => {
      if (cropIdSet.size > 0 && !cropIdSet.has(entry.crop.cropId)) return false;
      if (previousCropIdSet.size > 0 && (!entry.previousMainCrop || !previousCropIdSet.has(entry.previousMainCrop.cropId))) return false;
      if (filters.missingGrowingPeriod && entry.crop.seedingStartDate !== null && entry.crop.harvestEndDate !== null) return false;

      if (!hasPracticeFilter) return true;

      const tillages = entry.assignedPractices[PRACTICE_SLUG.TILLAGES];
      const organicFertilizers = entry.assignedPractices[PRACTICE_SLUG.ORGANIC_FERTILIZERS];
      const hasMissingTillageDepth =
        entry.isTillageDepthRequired &&
        tillages !== null &&
        tillages.length > 0 &&
        tillages.some((practice) => practice.tillageDepth === null && practice.assignedPracticeId !== ZERO_UUID);
      const hasMissingTillageEndDate =
        tillages !== null && tillages.length > 0 && tillages.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);
      const hasMissingOrganicFertilizerEndDate =
        organicFertilizers !== null &&
        organicFertilizers.length > 0 &&
        organicFertilizers.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);

      const missingSeedings = filters.missingSeedings && isMissingArray(entry.assignedPractices[PRACTICE_SLUG.SEEDINGS]);
      const missingTillages =
        filters.missingTillages && (hasMissingTillageDepth || hasMissingTillageEndDate || isMissingArray(entry.assignedPractices[PRACTICE_SLUG.TILLAGES]));
      const missingOrganicFertilizers =
        filters.missingOrganicFertilizers && (hasMissingOrganicFertilizerEndDate || isMissingArray(entry.assignedPractices[PRACTICE_SLUG.ORGANIC_FERTILIZERS]));

      // OR semantics: show a row if it matches any selected practice filter
      return missingSeedings || missingTillages || missingOrganicFertilizers;
    });
  }, [selectedYear, cropsWithMissingHistoricalPracticeDataByYear, filters]);

  const getMissingItemById = useCallback(
    (id: string): MissingDataForm | null => {
      if (!selectedYear) return null;

      const entry = filteredEntries.find((item) => item.crop.id === id);
      if (entry) {
        const crop = entry.crop ?? [];
        const seedings = entry.assignedPractices[PRACTICE_SLUG.SEEDINGS];
        const tillages = entry.assignedPractices[PRACTICE_SLUG.TILLAGES];
        const organicFertilizers = entry.assignedPractices[PRACTICE_SLUG.ORGANIC_FERTILIZERS];
        const {
          isGrowingPeriodEditable,
          isSeedingEditable,
          isTillageEditable,
          isTillageDepthEditable,
          areTillageDatesEditable,
          isOrganicEditable,
          areOrganicDatesEditable,
        } = getEditableState(
          entry,
          activeFarmReportingYear,
          isReportingYearLocked,
          undefined,
          isAdminOrImpersonator,
          submissionStatusValue?.lastSubmissionYear
        );

        return {
          key: entry.crop.id,
          fieldId: entry.fieldId,
          year: selectedYear,
          crop,
          seedings,
          tillages,
          organicFertilizers,
          isGrowingPeriodEditable,
          isSeedingEditable,
          isOrganicEditable,
          isTillageEditable,
          isTillageDepthEditable,
          areTillageDatesEditable,
          areOrganicDatesEditable,
        };
      }
      return null;
    },
    [selectedYear, filteredEntries, activeFarmReportingYear, isReportingYearLocked, isAdminOrImpersonator, submissionStatusValue?.lastSubmissionYear]
  );

  const selectableRowKeys = useMemo(() => {
    const fieldIds = new Set((fields ?? []).map((field) => field.id));

    return filteredEntries.filter((entry) => fieldIds.has(entry.fieldId)).map((entry) => entry.crop.id);
  }, [filteredEntries, fields]);

  const {
    expandedRowKeys,
    setExpandedRowKeys,
    toggleSetItem,
    selectedItems,
    setSelectedItems,
    isMultiSelectMode,
    setIsMultiSelectMode,
    onCheckBoxClicked,
    selectedRowCount,
    allRowsSelected,
    handleSelectAllClicked,
  } = useMissingDataTableSelection({ selectableRowKeys, getMissingItemById, selectedYear });

  const tableColumns = useMemo(() => {
    const columns: Array<{ dataIndex: string; key: string; width: string; title?: string | React.ReactNode }> = [
      {
        title: (
          <Flex align="center" justify="center">
            <Checkbox
              className="historical-practice-data-table-view-select-checkbox"
              checked={allRowsSelected}
              onChange={(event) => handleSelectAllClicked(event.target.checked)}
            />
          </Flex>
        ),
        dataIndex: "select",
        key: "select",
        width: "2rem",
      },
      {
        title: t("table.header.field"),
        dataIndex: "field",
        key: "field",
        width: "11rem",
      },
      {
        title: t(`table.header.${PRACTICE_SLUG.CROPS}`),
        dataIndex: PRACTICE_SLUG.CROPS,
        key: PRACTICE_SLUG.CROPS,
        width: "9.5rem",
      },
      {
        title: t("table.header.growingPeriod"),
        dataIndex: "growingPeriod",
        key: "growingPeriod",
        width: "8.5rem",
      },
    ];

    if (requiredPracticeSlugs.includes(PRACTICE_SLUG.SEEDINGS)) {
      columns.push({
        title: t(`table.header.${PRACTICE_SLUG.SEEDINGS}`),
        dataIndex: PRACTICE_SLUG.SEEDINGS,
        key: PRACTICE_SLUG.SEEDINGS,
        width: "8.5rem",
      });
    }

    if (requiredPracticeSlugs.includes(PRACTICE_SLUG.TILLAGES)) {
      columns.push({
        title: t(`table.header.${PRACTICE_SLUG.TILLAGES}`),
        dataIndex: PRACTICE_SLUG.TILLAGES,
        key: PRACTICE_SLUG.TILLAGES,
        width: "10rem",
      });
    }

    if (requiredPracticeSlugs.includes(PRACTICE_SLUG.ORGANIC_FERTILIZERS)) {
      columns.push({
        title: t(`table.header.${PRACTICE_SLUG.ORGANIC_FERTILIZERS}`),
        dataIndex: PRACTICE_SLUG.ORGANIC_FERTILIZERS,
        key: PRACTICE_SLUG.ORGANIC_FERTILIZERS,
        width: "10rem",
      });
    }

    columns.push({
      title: t("table.header.status"),
      dataIndex: "status",
      key: "status",
      width: "8rem",
    });

    return columns;
  }, [t, requiredPracticeSlugs, allRowsSelected, handleSelectAllClicked]);

  const tableData = useMemo(
    () =>
      selectedYear
        ? filteredEntries
            .map((entry) => {
              const field = fields?.find((field) => field.id === entry.fieldId);
              if (!field) {
                return null;
              }

              const crop = entry.crop ?? [];
              const seedings = entry.assignedPractices[PRACTICE_SLUG.SEEDINGS];
              const tillages = entry.assignedPractices[PRACTICE_SLUG.TILLAGES];
              const organicFertilizers = entry.assignedPractices[PRACTICE_SLUG.ORGANIC_FERTILIZERS];

              const hasMissingTillageDepth =
                entry.isTillageDepthRequired &&
                tillages !== null &&
                tillages.length > 0 &&
                tillages.some((practice) => practice.tillageDepth === null && practice.assignedPracticeId !== ZERO_UUID);
              const hasMissingTillageEndDate =
                tillages !== null && tillages.length > 0 && tillages.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);
              const hasMissingOrganicFertilizerEndDate =
                organicFertilizers !== null &&
                organicFertilizers.length > 0 &&
                organicFertilizers.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);

              const hasCompleteGrowingPeriod = entry.crop.seedingStartDate !== null && entry.crop.harvestEndDate !== null;
              const isStatusComplete =
                hasCompleteGrowingPeriod &&
                !hasMissingTillageDepth &&
                !hasMissingTillageEndDate &&
                !hasMissingOrganicFertilizerEndDate &&
                [seedings, tillages, organicFertilizers].every((column) => column === null || (Array.isArray(column) && column.length > 0));

              const isExpandedRow = expandedRowKeys.has(entry.crop.id);
              const isSelectedRow = selectedItems.has(entry.crop.id);
              const hasHiddenPractices = (seedings?.length ?? 0) > 1 || (tillages?.length ?? 0) > 1 || (organicFertilizers?.length ?? 0) > 1;

              const {
                isGrowingPeriodEditable,
                isSeedingEditable,
                isTillageEditable,
                isTillageDepthEditable,
                areTillageDatesEditable,
                isOrganicEditable,
                areOrganicDatesEditable,
              } = getEditableState(
                entry,
                activeFarmReportingYear,
                isReportingYearLocked,
                undefined,
                isAdminOrImpersonator,
                submissionStatusValue?.lastSubmissionYear
              );

              const missingItem: MissingDataForm = {
                key: entry.crop.id,
                fieldId: entry.fieldId,
                year: selectedYear,
                crop,
                seedings,
                tillages,
                organicFertilizers,
                isGrowingPeriodEditable,
                isSeedingEditable,
                isOrganicEditable,
                isTillageEditable,
                isTillageDepthEditable,
                areTillageDatesEditable,
                areOrganicDatesEditable,
              };

              return {
                key: entry.crop.id,
                select: (
                  <Flex align="center" justify="center" data-no-row-select>
                    <Checkbox
                      className="historical-practice-data-table-view-select-checkbox"
                      checked={isSelectedRow}
                      onChange={(e) => {
                        onCheckBoxClicked(entry.crop.id, !!e.nativeEvent.shiftKey);
                      }}
                    />
                  </Flex>
                ),
                field: (
                  <Flex justify="flex-start" align="center" gap="0.75rem">
                    <FieldMap field={field} />
                    <Flex justify="center" align="flex-start" vertical>
                      <PracticeTableTooltip title={field.name}>
                        {({ ref }) => (
                          <div ref={ref} className="margin-0 historical-practice-data-table-view-field-name">
                            {field.name}
                          </div>
                        )}
                      </PracticeTableTooltip>
                      <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                        {formatNumber({ number: field.size })} ha
                      </Typography>
                    </Flex>
                  </Flex>
                ),
                [PRACTICE_SLUG.CROPS]: (
                  <Flex vertical>
                    <PracticeTableTooltip title={cropLabel(entry.crop.translationSlug, entry.crop.cropName)}>
                      {({ ref }) => (
                        <div ref={ref} className="margin-0 historical-practice-data-table-view-crop-name">
                          {cropLabel(entry.crop.translationSlug, entry.crop.cropName)}
                        </div>
                      )}
                    </PracticeTableTooltip>
                    {!hideCropSubtext && (
                      <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                        {entry.previousMainCrop
                          ? t("table.data.previousCrop", { cropName: cropLabel(entry.previousMainCrop.translationSlug, entry.previousMainCrop.cropName) })
                          : t("table.data.na")}
                      </Typography>
                    )}
                  </Flex>
                ),
                growingPeriod:
                  entry.crop.seedingStartDate && entry.crop.harvestEndDate ? (
                    <PracticeChip
                      practiceName={`${moment(entry.crop.seedingStartDate).format("DD MMM")} – ${moment(entry.crop.harvestEndDate).format("DD MMM")}`}
                      hasMissingData={!hasCompleteGrowingPeriod}
                      isEditable={isGrowingPeriodEditable}
                      onClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.CROPS, true)}
                    />
                  ) : (
                    <Button
                      type="text"
                      className="historical-practice-data-table-view-add-practice-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddMissingItemClicked(missingItem, PRACTICE_SLUG.CROPS, false);
                      }}
                    >
                      {t("table.data.actions.add")}
                    </Button>
                  ),
                [PRACTICE_SLUG.SEEDINGS]: (
                  <PracticeColumn
                    practices={seedings}
                    practicesData={seedingPractices}
                    getPracticeId={(id) => id}
                    getItemKey={(id) => id}
                    isExpanded={isExpandedRow}
                    onClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.SEEDINGS, true)}
                    onAddClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.SEEDINGS, false)}
                    isEditable={isSeedingEditable}
                    practiceType={PRACTICE_SLUG.SEEDINGS}
                  />
                ),
                [PRACTICE_SLUG.TILLAGES]: (
                  <PracticeColumn
                    practices={tillages}
                    practicesData={tillagePractices}
                    getPracticeId={(tillage) => tillage.assignedPracticeId}
                    getItemKey={(tillage) => tillage.id || `tillage-${tillage.assignedPracticeId}`}
                    isExpanded={isExpandedRow}
                    onClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.TILLAGES, true)}
                    onAddClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.TILLAGES, false)}
                    isEditable={isTillageEditable || isTillageDepthEditable || areTillageDatesEditable}
                    practiceType={PRACTICE_SLUG.TILLAGES}
                    isTillageDepthRequired={entry.isTillageDepthRequired}
                  />
                ),
                [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: (
                  <PracticeColumn
                    practices={organicFertilizers}
                    practicesData={organicFertilizerPractices}
                    getPracticeId={(organicFertilizer) => organicFertilizer.assignedPracticeId}
                    getItemKey={(organicFertilizer) => organicFertilizer.id || `organic-${organicFertilizer.assignedPracticeId}`}
                    isExpanded={isExpandedRow}
                    onClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.ORGANIC_FERTILIZERS, true)}
                    onAddClick={() => onAddMissingItemClicked(missingItem, PRACTICE_SLUG.ORGANIC_FERTILIZERS, false)}
                    isEditable={isOrganicEditable || areOrganicDatesEditable}
                    practiceType={PRACTICE_SLUG.ORGANIC_FERTILIZERS}
                  />
                ),
                status: (
                  <Flex align="center" gap="0.5rem" className="historical-practice-data-table-view-status">
                    <Flex align="center" justify="center" className="historical-practice-data-table-view-status-container">
                      {isStatusComplete ? (
                        <Icon iconName="tabler/check" size="xs" className="historical-practice-data-table-view-status-icon-complete" />
                      ) : (
                        <span className="historical-practice-data-table-view-status-dot-incomplete" />
                      )}
                    </Flex>
                    <Typography variant="paragraph-s" className="no-wrap">
                      {t(isStatusComplete ? "table.data.status.complete" : "table.data.status.incomplete")}
                    </Typography>
                    {hasHiddenPractices && (
                      <span>
                        <IconButton
                          size="small"
                          shape="circle"
                          iconName={isExpandedRow ? "tabler/chevron-up" : "tabler/chevron-down"}
                          onClick={() => toggleSetItem(entry.crop.id, setExpandedRowKeys)}
                        />
                      </span>
                    )}
                  </Flex>
                ),
              };
            })
            .filter((entry) => entry !== null)
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedYear,
      filteredEntries,
      fields,
      expandedRowKeys,
      selectedItems,
      activeFarmReportingYear,
      isReportingYearLocked,
      isAdminOrImpersonator,
      submissionStatusValue?.lastSubmissionYear,
      t,
      cropLabel,
      seedingPractices,
      tillagePractices,
      organicFertilizerPractices,
      onCheckBoxClicked,
      hideCropSubtext,
    ]
  );

  useEffect(() => {
    const selectedMissingData = Array.from(selectedItems.values());
    const hasMissingGrowingPeriod = selectedMissingData.filter((item) => item.isGrowingPeriodEditable).length > 0;
    const hasMissingSeeding = requiredPracticeSlugs.includes(PRACTICE_SLUG.SEEDINGS) && selectedMissingData.filter((item) => item.isSeedingEditable).length > 0;
    const hasMissingTillage =
      requiredPracticeSlugs.includes(PRACTICE_SLUG.TILLAGES) &&
      selectedMissingData.filter((item) => item.isTillageEditable || item.isTillageDepthEditable).length > 0;
    const hasMissingOrganicFertilizer =
      requiredPracticeSlugs.includes(PRACTICE_SLUG.ORGANIC_FERTILIZERS) && selectedMissingData.filter((item) => item.isOrganicEditable).length > 0;
    setAddGrowingPeriodEnabled(hasMissingGrowingPeriod);
    setAddSeedingEnabled(hasMissingSeeding);
    setAddTillageEnabled(hasMissingTillage);
    setAddOrganicFertilizerEnabled(hasMissingOrganicFertilizer);
  }, [selectedItems, requiredPracticeSlugs]);

  const showNoMatchesEmptyState = selectedYear && tableData.length === 0 && hasFiltersActive && onClearFilters;

  if (showNoMatchesEmptyState) {
    return (
      <Flex justify="center" align="center" className="historical-practice-data-table-view-no-matches-empty-state-container">
        <NoMatchesFeedback handleClearFilters={onClearFilters} />
      </Flex>
    );
  }

  const handleBulkEdit = (missingDataTypes: MissingDataTypes) => {
    // TODO (Alp): Maybe filter here for all not editable data?
    const type: PracticeSlug = missingDataTypes as PracticeSlug;
    setDrawerItems(Array.from(selectedItems.values()));
    setSelectedType(type);
    setDrawerShouldValidateOnMount(true);
    setShowDrawerMissingDataDrawer(true);
  };

  const bulkPracticeMenuItems = getBulkPracticeMenuItems({
    growingPeriod: { label: t("historicalPracticeDataTableView:multiSelectBar.addGrowingPeriod"), isEnabled: addGrowingPeriodEnabled },
    seeding: { label: t("historicalPracticeDataTableView:multiSelectBar.addSeeding"), isEnabled: addSeedingEnabled },
    tillage: { label: t("historicalPracticeDataTableView:multiSelectBar.addTillage"), isEnabled: addTillageEnabled },
    organicFertilizer: { label: t("historicalPracticeDataTableView:multiSelectBar.addOrganicFertilizer"), isEnabled: addOrganicFertilizerEnabled },
    hidePracticeButtons,
    onSelect: handleBulkEdit,
  });

  const handleClearSelection = () => {
    setSelectedItems(new Map());
    setIsMultiSelectMode(false);
  };

  return selectedYear ? (
    <>
      <Table
        className="historical-practice-data-table-view-table-grid"
        columns={tableColumns}
        data={tableData}
        scroll={tableBodyHeight ? { y: tableBodyHeight } : undefined}
        onRow={(record) => ({
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            const target = e.target as HTMLElement;
            if (target.closest("[data-no-row-select]")) return;
            onCheckBoxClicked(record.key);
          },
          style: { cursor: "pointer" },
        })}
      />
      {drawerItems.length > 0 && selectedType && (
        <AddEditMissingDataDrawer
          showDrawer={showDrawerMissingDataDrawer}
          missingDataFormItems={drawerItems}
          onClose={() => {
            setShowDrawerMissingDataDrawer(false);
            setDrawerItems([]);
            setSelectedType(undefined);
          }}
          practiceType={selectedType}
          selectedYear={selectedYear}
          shouldValidateOnMount={drawerShouldValidateOnMount}
          onDatesCollected={onDatesCollected}
          onSubmitSuccess={clearSelectionsOnSubmit ? handleClearSelection : undefined}
          skipDbMutation={skipDbMutation}
        />
      )}
      {isMultiSelectMode &&
        selectedRowCount > 0 &&
        (isBulkEditingImprovementsEnabled ? (
          <BulkActionBar
            selectedCount={selectedRowCount}
            selectedFieldIds={Array.from(selectedItems.values()).map((item) => item.fieldId)}
            onClear={handleClearSelection}
            addDataMenuItems={bulkPracticeMenuItems}
            editDataMenuItems={bulkPracticeMenuItems}
          />
        ) : (
          <MultiCropSelectionBarMissingData
            selectedCount={selectedRowCount}
            onEdit={handleBulkEdit}
            onClear={handleClearSelection}
            addGrowingPeriodEnabled={addGrowingPeriodEnabled}
            addSeedingEnabled={addSeedingEnabled}
            addTillageEnabled={addTillageEnabled}
            addOrganicFertilizerEnabled={addOrganicFertilizerEnabled}
            hidePracticeButtons={hidePracticeButtons}
          />
        ))}
    </>
  ) : null;
};
